/**
 * "% rent paid this month" stat for one property, shown as a pill on
 * PropertyCard.
 *
 * Expected = sum of the CURRENT MONTH's billed rent per collection-eligible
 * booking, including notice and pending move-out tenants: `expectedRentCentsForMonth`, the
 * same first-month proration / override / active-discount (COL-360) rule
 * `repriceRentRows` uses to price actual `payments` rows, not the flat
 * contractual monthly rent — a tenant who moved in mid-month is only billed
 * the pro-rata amount, and a discounted tenant who fully pays their (lower)
 * discounted rent must not read as "partially paid" against the undiscounted
 * base.
 * Paid = verified receipts applied to each current-month rent row, including
 * partial receipts, capped at the booking's expected rent. Fully settled rows
 * retain their existing settlement rules. Occupancy and future billing use
 * separate eligibility rules.
 *
 * The rent query is bounded to the current month (`gte`/`lt` on `due_date`,
 * as `useApartmentMonthRent` does) rather than paging each booking's whole
 * ledger via `fetchRentLedgerForBookings` — that helper is for surfaces that
 * need past months too (Reminders); a card grid re-running a full-history
 * fetch per visible property would be needlessly slow.
 *
 * Room and booking ids are looked up through `String(...)`-keyed maps:
 * PostgREST doesn't consistently narrow bigint columns to JS numbers across
 * separate queries (see `useProperties.ts`'s `roomsByProp` comment for the
 * same observed variance), so a raw `number`-keyed map can silently miss a
 * match and undercount.
 */
import { isRentCollectionEligible } from "@/lib/rent-collection";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  getFullMonthlyRentFromBooking,
  resolveRentSettlement,
  latestProofByPaymentId,
} from "@/lib/rent";
import { expectedRentCentsForMonth } from "@/lib/rent-room-sync";
import { nextMonthStart } from "@/lib/payment-booking";
import { currentMonthKey } from "@/hooks/v2/useApartmentMonthRent";
import {
  untypedSupabase,
  chunkPropertyIds,
  effectiveStartDate,
  ROW_PAGE_SIZE,
} from "@/hooks/v2/useProperties";

export interface PropertyRentPaidStats {
  /** Number of collection-eligible tenancies. */
  activeTenancies: number;
  /** Sum of contracted monthly rent across those bookings, in EUR. */
  expectedEur: number;
  /** Sum of effectively-paid current-month rent across those bookings, in EUR. */
  paidEur: number;
  /** round(paidEur / expectedEur * 100), or null when there's nothing expected. */
  pct: number | null;
}

const EMPTY: PropertyRentPaidStats = {
  activeTenancies: 0,
  expectedEur: 0,
  paidEur: 0,
  pct: null,
};

export const PROPERTY_RENT_PAID_STATS_KEY = "property-rent-paid-stats";

interface BookingRow {
  id: number;
  room_id: number | string | null;
  monthly_rent: number | null;
  first_month_rent_override: number | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  admin_archived_at: string | null;
  actual_move_in_date: string | null;
  move_in_date: string | null;
  actual_move_out_date: string | null;
  move_out_date: string | null;
  rent_discount_mode: string | null;
  rent_discount_value: number | null;
  rent_discount_months: number | null;
  rent_discount_start_month: string | null;
}

interface MonthPaymentRow {
  id: string;
  booking_id: number | string | null;
  amount_cents: number | null;
  received_cents?: number | null;
  status: string | null;
  due_date: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  marked_paid_at: string | null;
  marked_paid_by: string | null;
}

/** Tenancies still in rent collection, including notice and move-out processing. */
function filterActiveBookings(bookings: BookingRow[], now: Date): BookingRow[] {
  return bookings.filter((b) =>
    isRentCollectionEligible(
      {
        status: b.status ?? "",
        admin_archived_at: b.admin_archived_at,
        actual_move_in_date: b.actual_move_in_date,
        move_in_date: b.move_in_date,
        start_date: b.start_date,
        actual_move_out_date: b.actual_move_out_date,
        move_out_date: b.move_out_date,
        end_date: b.end_date,
      },
      now
    )
  );
}

/** This month's billed rent for one active booking, in EUR (see file header for the proration/discount rule). */
function expectedEurForBooking(
  b: BookingRow,
  priceByRoom: Map<string, number | null>,
  now: Date
): number {
  const fullMonthlyRent = getFullMonthlyRentFromBooking({
    monthly_rent: b.monthly_rent,
    rooms: {
      price: b.room_id != null ? priceByRoom.get(String(b.room_id)) : null,
    },
  });
  return (
    expectedRentCentsForMonth({
      fullMonthlyRent,
      moveInIso: effectiveStartDate(b),
      firstMonthOverride: b.first_month_rent_override,
      dueYear: now.getFullYear(),
      dueMonthIndex0: now.getMonth(),
      discount: {
        rent_discount_mode: b.rent_discount_mode,
        rent_discount_value: b.rent_discount_value,
        rent_discount_months: b.rent_discount_months,
        rent_discount_start_month: b.rent_discount_start_month,
      },
    }) / 100
  );
}

/**
 * Every row of `table` matching `column IN values`, chunked (URL length) and
 * paged (PostgREST's per-response row cap) the same way `useProperties.ts`'s
 * `fetchAllByPropertyId` reads rooms/apartments — an unpaged `.in()` silently
 * truncates once a portfolio-wide query crosses that cap, which would read as
 * "no active tenant" for whichever properties' rows fell past it.
 */
export async function fetchAllByColumn<Row>(
  table: string,
  select: string,
  column: string,
  values: Array<number | string>,
  orderColumn: string,
  ascending: boolean,
  extraFilter?: (query: any) => any,
  /** Ids per `.in()`. Pass 100 for UUID columns: 200 UUIDs is a ~7 KB URL. */
  chunkSize?: number
): Promise<Row[]> {
  if (!values.length) return [];
  const out: Row[] = [];
  for (const chunk of chunkPropertyIds(values, chunkSize)) {
    let from = 0;
    for (;;) {
      let query = untypedSupabase.from(table).select(select).in(column, chunk);
      if (extraFilter) query = extraFilter(query);
      let ordered = query.order(orderColumn, { ascending });
      // A page is only a stable window over the table if the ordering is
      // total. `created_at` is not unique, so two proofs sharing a timestamp
      // across a page boundary could be repeated or dropped — and a dropped
      // proof silently changes which one `latestProofByPaymentId` calls the
      // latest, i.e. whether a payment reads as paid. `id` breaks the tie.
      if (orderColumn !== "id")
        ordered = ordered.order("id", { ascending: true });
      const { data, error } = await ordered.range(
        from,
        from + ROW_PAGE_SIZE - 1
      );
      if (error) throw new Error(`Could not load ${table}: ${error.message}`);
      const rows = (data ?? []) as Row[];
      out.push(...rows);
      if (rows.length < ROW_PAGE_SIZE) break;
      from += ROW_PAGE_SIZE;
    }
  }
  return out;
}

/**
 * Effectively-paid current-month rent for the given bookings, in EUR, keyed by
 * booking id (as a string, per the file header's PostgREST bigint note).
 * `expectedByBooking` gates which bookings actually have something due — a
 * payment row for a booking that priced to 0 this month must not count.
 */
async function fetchPaidEurByBooking(
  bookingIds: Array<number | string>,
  expectedByBooking: Map<string, number>,
  month: string
): Promise<Map<string, number>> {
  const paidByBooking = new Map<string, number>();
  if (!bookingIds.length) return paidByBooking;

  const payments = await fetchAllByColumn<MonthPaymentRow>(
    "payments",
    "id, booking_id, amount_cents, received_cents, status, due_date, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by",
    "booking_id",
    bookingIds,
    "id",
    true,
    (query) =>
      query
        .eq("type", "rent")
        .gte("due_date", `${month}-01`)
        .lt("due_date", nextMonthStart(month))
  );
  if (!payments.length) return paidByBooking;

  const proofRows = await fetchAllByColumn<{
    payment_id: string;
    status: string | null;
  }>(
    "payment_proofs",
    "payment_id, status, created_at",
    "payment_id",
    payments.map((p) => p.id),
    "created_at",
    false
  );
  const proofByPayment = latestProofByPaymentId(proofRows);

  for (const row of payments) {
    if (row.booking_id == null) continue;
    const key = String(row.booking_id);
    if (!expectedByBooking.has(key)) continue;
    if (row.status === "canceled" || row.status === "failed") continue;
    const settlement = resolveRentSettlement(row, (id) =>
      proofByPayment.get(id)
    );
    paidByBooking.set(
      key,
      Math.min(
        expectedByBooking.get(key) ?? 0,
        (paidByBooking.get(key) ?? 0) + settlement.appliedCents / 100
      )
    );
  }
  return paidByBooking;
}

export async function fetchPropertyRentPaidStats(
  propertyId: number,
  now: Date = new Date()
): Promise<PropertyRentPaidStats> {
  const { data: roomsRes, error: roomsErr } = await supabase
    .from("rooms")
    .select("id, price")
    .eq("property_id", propertyId);
  if (roomsErr) throw new Error(`Could not load rooms: ${roomsErr.message}`);
  const rooms = roomsRes ?? [];
  const roomIds = rooms.map((r) => r.id);
  if (!roomIds.length) return EMPTY;
  const priceByRoom = new Map<string, number | null>();
  for (const r of rooms) priceByRoom.set(String(r.id), r.price);

  const { data: bookingsRes, error: bookingsErr } = await supabase
    .from("bookings")
    .select(
      "id, room_id, monthly_rent, first_month_rent_override, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date, rent_discount_mode, rent_discount_value, rent_discount_months, rent_discount_start_month"
    )
    .in("room_id", roomIds);
  if (bookingsErr)
    throw new Error(`Could not load bookings: ${bookingsErr.message}`);
  const bookings = (bookingsRes ?? []) as unknown as BookingRow[];

  const active = filterActiveBookings(bookings, now);
  if (!active.length) return EMPTY;

  const expectedByBooking = new Map<string, number>();
  let expectedEur = 0;
  for (const b of active) {
    const rent = expectedEurForBooking(b, priceByRoom, now);
    expectedByBooking.set(String(b.id), rent);
    expectedEur += rent;
  }
  if (expectedEur <= 0) return EMPTY;

  const month = currentMonthKey(now);
  const paidByBooking = await fetchPaidEurByBooking(
    active.map((b) => b.id),
    expectedByBooking,
    month
  );
  let paidEur = 0;
  for (const amount of paidByBooking.values()) paidEur += amount;

  const pct = Math.round((paidEur / expectedEur) * 100);
  return { activeTenancies: active.length, expectedEur, paidEur, pct };
}

/**
 * Same stat as `fetchPropertyRentPaidStats`, batched across many properties in
 * one fetch pass (rooms → bookings → payments → proofs, each chunked/paged as
 * needed — see `fetchAllByColumn`) instead of a separate rooms→bookings→
 * payments→proofs round trip per property (N+1) — needed so the properties
 * grid can sort by outstanding rent BEFORE pagination, which means knowing
 * every filtered property's stat up front rather than lazily per visible card.
 */
export async function fetchAllPropertiesRentPaidStats(
  propertyIds: number[],
  now: Date = new Date()
): Promise<Map<number, PropertyRentPaidStats>> {
  const result = new Map<number, PropertyRentPaidStats>();
  if (!propertyIds.length) return result;

  const rooms = await fetchAllByColumn<{
    id: number | string;
    price: number | null;
    property_id: number | string;
  }>("rooms", "id, price, property_id", "property_id", propertyIds, "id", true);
  if (!rooms.length) {
    for (const id of propertyIds) result.set(id, EMPTY);
    return result;
  }
  const priceByRoom = new Map<string, number | null>();
  const propertyByRoom = new Map<string, number>();
  for (const r of rooms) {
    priceByRoom.set(String(r.id), r.price);
    propertyByRoom.set(String(r.id), Number(r.property_id));
  }
  const roomIds = rooms.map((r) => r.id);

  const bookings = await fetchAllByColumn<BookingRow>(
    "bookings",
    "id, room_id, monthly_rent, first_month_rent_override, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date, rent_discount_mode, rent_discount_value, rent_discount_months, rent_discount_start_month",
    "room_id",
    roomIds,
    "id",
    true
  );

  const active = filterActiveBookings(bookings, now);
  if (!active.length) {
    for (const id of propertyIds) result.set(id, EMPTY);
    return result;
  }

  const expectedByBooking = new Map<string, number>();
  const bookingToProperty = new Map<string, number>();
  const expectedByProperty = new Map<number, number>();
  const activeCountByProperty = new Map<number, number>();
  for (const b of active) {
    const propId =
      b.room_id != null ? propertyByRoom.get(String(b.room_id)) : undefined;
    if (propId == null) continue;
    const rent = expectedEurForBooking(b, priceByRoom, now);
    const bookingKey = String(b.id);
    expectedByBooking.set(bookingKey, rent);
    bookingToProperty.set(bookingKey, propId);
    expectedByProperty.set(
      propId,
      (expectedByProperty.get(propId) ?? 0) + rent
    );
    activeCountByProperty.set(
      propId,
      (activeCountByProperty.get(propId) ?? 0) + 1
    );
  }

  const month = currentMonthKey(now);
  const paidByBooking = await fetchPaidEurByBooking(
    active.map((b) => b.id),
    expectedByBooking,
    month
  );
  const paidByProperty = new Map<number, number>();
  for (const [bookingKey, amount] of paidByBooking) {
    const propId = bookingToProperty.get(bookingKey);
    if (propId == null) continue;
    paidByProperty.set(propId, (paidByProperty.get(propId) ?? 0) + amount);
  }

  for (const id of propertyIds) {
    const activeTenancies = activeCountByProperty.get(id) ?? 0;
    const expectedEur = expectedByProperty.get(id) ?? 0;
    if (activeTenancies === 0 || expectedEur <= 0) {
      result.set(id, EMPTY);
      continue;
    }
    const paidEur = paidByProperty.get(id) ?? 0;
    const pct = Math.round((paidEur / expectedEur) * 100);
    result.set(id, { activeTenancies, expectedEur, paidEur, pct });
  }
  return result;
}

export function usePropertyRentPaidStats(
  propertyId: number | null,
  opts: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: [PROPERTY_RENT_PAID_STATS_KEY, propertyId],
    enabled: propertyId != null && (opts.enabled ?? true),
    queryFn: () =>
      propertyId == null
        ? Promise.resolve(EMPTY)
        : fetchPropertyRentPaidStats(propertyId),
    staleTime: 30_000,
  });
}

/**
 * Rent-paid stats for every given property in one batched fetch, keyed by
 * property id. Used where the stat must be known for a whole list up front
 * (e.g. sorting properties by outstanding rent before pagination) rather than
 * lazily per visible card.
 *
 * Keyed under the SAME `PROPERTY_RENT_PAID_STATS_KEY` prefix as
 * `usePropertyRentPaidStats` (not a separate key) so every existing
 * `invalidateQueries({ queryKey: [PROPERTY_RENT_PAID_STATS_KEY] })` call across
 * the app (mark-paid, reminders, payment edits, etc.) invalidates this
 * aggregate too — react-query's default matching is a prefix match, so a
 * different top-level key here would let the sort order go stale right after
 * the exact actions that should move a property up or down it.
 */
export function useAllPropertiesRentPaidStats(
  propertyIds: number[],
  opts: { enabled?: boolean } = {}
) {
  const sortedIds = [...propertyIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: [PROPERTY_RENT_PAID_STATS_KEY, "all", sortedIds],
    enabled: sortedIds.length > 0 && (opts.enabled ?? true),
    queryFn: () => fetchAllPropertiesRentPaidStats(sortedIds),
    staleTime: 30_000,
  });
}
